import React, { useCallback, useEffect, useId } from "react"

import { XMarkIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import { createPortal } from "react-dom"

type AriaAttribute = `aria-${string}`

type ForwardedDialogProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  AriaAttribute | `children` | `className` | `id` | `onClose` | `open` | `title`
>

export type DrawerRenderHeaderProps = {
  close: () => void
  titleId: string
  descriptionId: string
}

export type DrawerTheme = `light` | `dark`

export type DrawerProps = ForwardedDialogProps & {
  open: boolean
  onClose: (open: false) => void
  title: React.ReactNode
  titleSrOnly?: boolean
  description?: React.ReactNode
  children: React.ReactNode
  widthClass?: string
  contentClassName?: string
  hideCloseButton?: boolean
  header?: (props: DrawerRenderHeaderProps) => React.ReactNode
  theme?: DrawerTheme
}

const IS_DEV_BUILD = process.env.NODE_ENV !== `production`

const drawerLayerStyle: React.CSSProperties = {
  position: `fixed`,
  inset: 0,
  zIndex: 2147483000,
}

const LIGHT_BACKDROP = `rgba(0, 33, 96, 0.3)`
const DARK_BACKDROP = `rgba(2, 4, 10, 0.72)`

const drawerBackdropStyle = (theme: DrawerTheme): React.CSSProperties => ({
  ...drawerLayerStyle,
  backgroundColor: theme === `dark` ? DARK_BACKDROP : LIGHT_BACKDROP,
})

const drawerSurfaceStyle: React.CSSProperties = {
  height: `100vh`,
  backgroundColor: `var(--brand-bg)`,
}

export const Drawer = React.forwardRef(function Drawer(
  {
    open,
    onClose,
    title,
    titleSrOnly = false,
    description,
    children,
    widthClass = `sm:max-w-lg`,
    contentClassName,
    hideCloseButton = false,
    header,
    theme = `light`,
    ...dialogProps
  }: DrawerProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const titleId = useId()
  const descriptionId = useId()

  if (IS_DEV_BUILD && !title) {
    console.error(
      `<Drawer> requires a 'title' prop to provide an accessible dialog name.`,
    )
  }

  const handleClose = useCallback(() => onClose(false), [onClose])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === `Escape`) {
        handleClose()
      }
    }

    document.addEventListener(`keydown`, handleKeyDown)

    return () => {
      document.removeEventListener(`keydown`, handleKeyDown)
    }
  }, [handleClose, open])

  if (!open || typeof document === `undefined`) {
    return null
  }

  return createPortal(
    <div
      {...dialogProps}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
      data-slot="drawer-root"
      data-theme={theme === `dark` ? `dark` : undefined}
      data-battery-theme={theme === `dark` ? `dark` : undefined}
      style={drawerLayerStyle}
      // Above legacy proposal modals/notification layers (max 999999) so
      // route-level drawers cannot open underneath an older portal underlay,
      // but below the dev-only Agentation overrides in global.css.
      className="fixed inset-0 z-[2147483000]"
    >
      <div
        data-slot="drawer-backdrop"
        style={drawerBackdropStyle(theme)}
        onClick={handleClose}
        className={clsx(
          `fixed inset-0 z-[2147483000]`,
          theme === `dark` ? `bg-[rgba(2,4,10,0.72)]` : `bg-[rgba(0,33,96,0.3)]`,
        )}
      />

      <div
        data-slot="drawer-container"
        style={drawerLayerStyle}
        className="pointer-events-none fixed inset-0 z-[2147483000] overflow-hidden"
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <div
              ref={ref}
              data-slot="drawer-panel"
              style={drawerSurfaceStyle}
              className={clsx(
                `pointer-events-auto relative h-screen w-screen transform bg-background supports-[height:100dvh]:h-dvh`,
                widthClass,
              )}
            >
              {!hideCloseButton ? (
                <div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4">
                  <button
                    type="button"
                    data-slot="drawer-close"
                    onClick={handleClose}
                    className="relative rounded-md text-muted transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                  >
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon aria-hidden="true" className="size-6" />
                  </button>
                </div>
              ) : null}

              <div
                data-slot="drawer-surface"
                style={drawerSurfaceStyle}
                className={clsx(
                  `relative flex h-screen flex-col overflow-y-auto bg-background shadow-xl supports-[height:100dvh]:h-dvh`,
                  header ? `py-0` : `py-6`,
                )}
              >
                {header ? (
                  <>
                    <h2
                      id={titleId}
                      data-slot="drawer-title"
                      className="sr-only"
                    >
                      {title}
                    </h2>
                    {description ? (
                      <p
                        id={descriptionId}
                        data-slot="drawer-description"
                        className="sr-only"
                      >
                        {description}
                      </p>
                    ) : null}
                    {header({
                      close: handleClose,
                      titleId,
                      descriptionId,
                    })}
                  </>
                ) : (
                  <div
                    data-slot="drawer-header"
                    className={clsx(
                      `px-4 sm:px-6`,
                      // The rule is what separates the title block from the
                      // body; the drawers that supply their own header band
                      // already draw one. A sr-only title with no description
                      // leaves nothing above the line, so it gets no band.
                      titleSrOnly && description == null
                        ? undefined
                        : `border-b border-border pb-5`,
                    )}
                  >
                    <h2
                      id={titleId}
                      data-slot="drawer-title"
                      // --step-1 lands ~22-25px, matching the drawer titles
                      // that carry their own header. The old sm: step shrank
                      // the title to 16px on the wide screens these open on.
                      className="font-display text-[length:var(--step-1)] leading-tight font-semibold text-foreground"
                    >
                      {titleSrOnly ? (
                        <span className="sr-only">{title}</span>
                      ) : (
                        title
                      )}
                    </h2>
                    {description ? (
                      <p
                        id={descriptionId}
                        data-slot="drawer-description"
                        className="mt-2 text-[length:var(--step--1)] leading-6 text-muted"
                      >
                        {description}
                      </p>
                    ) : null}
                  </div>
                )}
                <div
                  className={clsx(
                    `relative mt-6 flex-1 px-4 sm:px-6`,
                    contentClassName,
                  )}
                >
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
})
